import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import {
  doc,
  updateDoc,
  onSnapshot,
  collection,
  addDoc,
  query,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import TeamProgress from './TeamProgress';
import PartyChat from './PartyChat';
import DigitalPuzzle from './DigitalPuzzle';
import './PartySession.css';

function PartySession({ party, user, onLeave }) {
  const [partyData, setPartyData] = useState(party);
  const [showInvite, setShowInvite] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [mySection, setMySection] = useState(null);
  const [piecesToAdd, setPiecesToAdd] = useState('');
  const [copied, setCopied] = useState(false);
  const [puzzleMode, setPuzzleMode] = useState('physical'); // 'physical' or 'digital'

  const isHost = partyData.hostId === user.uid;

  // Listen for party updates
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'puzzleParties', party.id),
      (doc) => {
        if (doc.exists()) {
          setPartyData({ id: doc.id, ...doc.data() });

          // Find user's section
          const me = doc.data().participants?.find((p) => p.odId === user.uid);
          if (me?.section !== undefined) {
            setMySection(me.section);
          }
        }
      }
    );

    return () => unsubscribe();
  }, [party.id, user.uid]);

  const handleStartParty = async () => {
    try {
      await updateDoc(doc(db, 'puzzleParties', party.id), {
        status: 'active',
        startedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error starting party:', err);
    }
  };

  const handleClaimSection = async (sectionNum) => {
    try {
      // Update participant's section
      const updatedParticipants = partyData.participants.map((p) => {
        if (p.odId === user.uid) {
          return { ...p, section: sectionNum };
        }
        return p;
      });

      await updateDoc(doc(db, 'puzzleParties', party.id), {
        participants: updatedParticipants,
      });

      setMySection(sectionNum);
    } catch (err) {
      console.error('Error claiming section:', err);
    }
  };

  const handleAddPieces = async () => {
    const pieces = parseInt(piecesToAdd);
    if (isNaN(pieces) || pieces <= 0) return;

    try {
      // Update participant's pieces and total
      const updatedParticipants = partyData.participants.map((p) => {
        if (p.odId === user.uid) {
          return { ...p, piecesPlaced: (p.piecesPlaced || 0) + pieces };
        }
        return p;
      });

      const newTotal = (partyData.totalPiecesPlaced || 0) + pieces;

      await updateDoc(doc(db, 'puzzleParties', party.id), {
        participants: updatedParticipants,
        totalPiecesPlaced: newTotal,
        ...(newTotal >= partyData.totalPieces
          ? { status: 'completed', completedAt: serverTimestamp() }
          : {}),
      });

      // Log the activity
      await addDoc(collection(db, 'puzzleParties', party.id, 'activity'), {
        type: 'pieces_added',
        userId: user.uid,
        userName: user.displayName || user.email.split('@')[0],
        pieces: pieces,
        timestamp: serverTimestamp(),
      });

      setPiecesToAdd('');
    } catch (err) {
      console.error('Error adding pieces:', err);
    }
  };

  const handleEndParty = async () => {
    if (!window.confirm('Are you sure you want to end this party?')) return;

    try {
      await updateDoc(doc(db, 'puzzleParties', party.id), {
        status: 'ended',
        endedAt: serverTimestamp(),
      });
      onLeave();
    } catch (err) {
      console.error('Error ending party:', err);
    }
  };

  const handleLeaveParty = async () => {
    if (!window.confirm('Are you sure you want to leave this party?')) return;

    try {
      const updatedParticipants = partyData.participants.filter(
        (p) => p.odId !== user.uid
      );
      const updatedIds = partyData.participantIds.filter(
        (id) => id !== user.uid
      );

      await updateDoc(doc(db, 'puzzleParties', party.id), {
        participants: updatedParticipants,
        participantIds: updatedIds,
      });

      onLeave();
    } catch (err) {
      console.error('Error leaving party:', err);
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(partyData.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Calculate progress
  const progressPercent = Math.min(
    100,
    Math.round(
      ((partyData.totalPiecesPlaced || 0) / partyData.totalPieces) * 100
    )
  );

  // Get claimed sections
  const claimedSections =
    partyData.participants
      ?.filter((p) => p.section !== null && p.section !== undefined)
      .map((p) => p.section) || [];

  return (
    <div className="party-session">
      {/* Header */}
      <header className="party-header">
        <div className="party-title">
          <span className="party-icon">🧩</span>
          <div>
            <h1>{partyData.name}</h1>
            <p className="party-status">
              {partyData.status === 'waiting'
                ? '⏳ Waiting to start'
                : partyData.status === 'completed'
                ? '🎉 Completed!'
                : '🎯 In Progress'}
            </p>
          </div>
        </div>
        <div className="party-actions">
          <button
            className="invite-btn"
            onClick={() => setShowInvite(!showInvite)}
          >
            👥 Invite
          </button>
          <button className="chat-btn" onClick={() => setShowChat(!showChat)}>
            💬 Chat
          </button>
          {isHost ? (
            <button className="end-btn" onClick={handleEndParty}>
              End
            </button>
          ) : (
            <button className="leave-btn" onClick={handleLeaveParty}>
              Leave
            </button>
          )}
        </div>
      </header>

      {/* Invite Panel */}
      {showInvite && (
        <div className="invite-panel">
          <div className="invite-code">
            <span>Party Code:</span>
            <strong>{partyData.code}</strong>
            <button onClick={copyInviteCode}>
              {copied ? '✓ Copied!' : '📋 Copy'}
            </button>
          </div>
          <div className="participants-list">
            <h3>Puzzlers ({partyData.participants?.length || 1})</h3>
            {partyData.participants?.map((p, idx) => (
              <div key={idx} className="participant">
                <span className="participant-avatar">
                  {p.odId === partyData.hostId ? '👑' : '🧩'}
                </span>
                <span className="participant-name">{p.name}</span>
                <span className="participant-section">
                  {p.section !== null && p.section !== undefined
                    ? `Section ${p.section + 1}`
                    : 'No section'}
                </span>
                <span className="participant-pieces">
                  {p.piecesPlaced || 0} pieces
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Puzzle Mode Toggle */}
      {partyData.status === 'active' && (
        <div className="puzzle-mode-toggle">
          <button
            className={`mode-btn ${puzzleMode === 'physical' ? 'active' : ''}`}
            onClick={() => setPuzzleMode('physical')}
          >
            📦 Physical Puzzle
          </button>
          <button
            className={`mode-btn ${puzzleMode === 'digital' ? 'active' : ''}`}
            onClick={() => setPuzzleMode('digital')}
          >
            🎮 Digital Puzzle
          </button>
        </div>
      )}

      {/* Digital Puzzle Mode */}
      {puzzleMode === 'digital' && partyData.status === 'active' && (
        <div className="digital-puzzle-container">
          <DigitalPuzzle
            user={user}
            onComplete={(result) => {
              console.log('Puzzle completed!', result);
              // Could save result to party data
            }}
            onProgress={(progress) => {
              console.log('Puzzle progress:', progress);
            }}
          />
        </div>
      )}

      {/* Physical Puzzle Mode - Progress Overview */}
      {puzzleMode === 'physical' && (
        <>
          <div className="progress-overview">
            <div className="progress-header">
              <h2>🎯 Overall Progress</h2>
              <span className="progress-numbers">
                {partyData.totalPiecesPlaced || 0} / {partyData.totalPieces}{' '}
                pieces
              </span>
            </div>
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="progress-percent">{progressPercent}% Complete</div>
          </div>

          {/* Section Selection (if not claimed) */}
          {partyData.status !== 'waiting' && mySection === null && (
            <div className="section-selection">
              <h3>📦 Choose Your Section</h3>
              <p>Pick a section to work on:</p>
              <div className="sections-grid">
                {Array.from({ length: partyData.numSections }, (_, i) => {
                  const isClaimed = claimedSections.includes(i);
                  const claimer = partyData.participants?.find(
                    (p) => p.section === i
                  );
                  return (
                    <button
                      key={i}
                      className={`section-btn ${
                        isClaimed ? 'claimed' : 'available'
                      }`}
                      onClick={() => !isClaimed && handleClaimSection(i)}
                      disabled={isClaimed}
                    >
                      <span className="section-number">#{i + 1}</span>
                      <span className="section-pieces">
                        ~{partyData.piecesPerSection} pcs
                      </span>
                      {isClaimed && (
                        <span className="section-claimer">{claimer?.name}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* My Section Progress */}
          {mySection !== null && (
            <div className="my-section">
              <h3>📦 My Section: #{mySection + 1}</h3>
              <div className="add-pieces-form">
                <input
                  type="number"
                  value={piecesToAdd}
                  onChange={(e) => setPiecesToAdd(e.target.value)}
                  placeholder="Pieces placed"
                  min="1"
                  max={partyData.piecesPerSection}
                />
                <button
                  className="add-pieces-btn"
                  onClick={handleAddPieces}
                  disabled={!piecesToAdd || parseInt(piecesToAdd) <= 0}
                >
                  ➕ Add Pieces
                </button>
              </div>
            </div>
          )}

          {/* Team Progress */}
          <TeamProgress
            participants={partyData.participants || []}
            totalPieces={partyData.totalPieces}
            piecesPerSection={partyData.piecesPerSection}
          />
        </>
      )}

      {/* Chat Panel */}
      {showChat && (
        <PartyChat
          partyId={party.id}
          user={user}
          onClose={() => setShowChat(false)}
        />
      )}

      {/* Start Party Button (Host only, waiting status) */}
      {isHost && partyData.status === 'waiting' && (
        <div className="start-party-bar">
          <button className="start-party-btn" onClick={handleStartParty}>
            🎉 Start Puzzle Party!
          </button>
        </div>
      )}

      {/* Waiting Message (Non-host, waiting status) */}
      {!isHost && partyData.status === 'waiting' && (
        <div className="waiting-message">
          <p>⏳ Waiting for {partyData.hostName} to start the party...</p>
          <p>Get ready to puzzle! 🧩</p>
        </div>
      )}

      {/* Completed Celebration */}
      {partyData.status === 'completed' && (
        <div className="completed-celebration">
          <div className="celebration-icon">🎉</div>
          <h2>Puzzle Complete!</h2>
          <p>Amazing teamwork, everyone!</p>
          <div className="final-stats">
            <div className="stat">
              <span className="stat-value">{partyData.totalPieces}</span>
              <span className="stat-label">Pieces</span>
            </div>
            <div className="stat">
              <span className="stat-value">
                {partyData.participants?.length || 1}
              </span>
              <span className="stat-label">Puzzlers</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartySession;
